import { useEffect, useMemo, useRef, useState } from "react";
import { X, Eye, Save, Loader2, Upload, Trash2 } from "lucide-react";
import { authService } from "../../../services/api";
import { useNotification } from "../../../components/Notifications/NotificationContext";
import { useBotPhotos } from "../../../hooks/useBotPhotos";
import Loading from "../../../components/UI/Loading/Loading";
import InterstitialPreview from "./InterstitialPreview";
import {
    DEFAULT_CONFIG, PROFILE_TEMPLATES, INTERACTIVE_TEMPLATES, LIMITS, COUNTDOWN_MIN, COUNTDOWN_MAX,
    fieldsFor, normalizeConfig, buildConfigPayload, validateAvatarFile, templateGroup,
} from "./interstitialConfig";

const GROUPS = [
    { id: "profile", label: "Perfil", hint: "Card com foto, nome e botão do Telegram." },
    { id: "interactive", label: "Interativos", hint: "O visitante interage antes de ser redirecionado." },
];

const inputCls =
    "w-full h-11 md:h-10 px-3.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-base md:text-sm text-white placeholder:text-white/25 outline-none transition hover:border-white/[0.14] focus:border-[rgba(139,92,246,0.45)]";
const labelCls = "block text-xs font-medium text-white/60 mb-1.5";

function Toggle({ checked, onChange, label }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label={label}
            onClick={() => onChange(!checked)}
            className={`relative w-10 h-6 rounded-full border transition-colors flex-shrink-0 ${checked ? "bg-[var(--brand-500,#8b5cf6)] border-[var(--brand-400,#a78bfa)]" : "bg-white/10 border-white/20"}`}
        >
            <span className={`absolute top-[3px] w-4 h-4 rounded-full bg-white shadow-sm transition-all ${checked ? "left-[19px]" : "left-[3px]"}`} />
        </button>
    );
}

// Só o visual do toggle (para quando o elemento clicável é o container).
function ToggleKnob({ checked }) {
    return (
        <span aria-hidden="true" className={`relative w-10 h-6 rounded-full border transition-colors flex-shrink-0 ${checked ? "bg-[var(--brand-500,#8b5cf6)] border-[var(--brand-400,#a78bfa)]" : "bg-white/10 border-white/20"}`}>
            <span className={`absolute top-[3px] w-4 h-4 rounded-full bg-white shadow-sm transition-all ${checked ? "left-[19px]" : "left-[3px]"}`} />
        </span>
    );
}

function ToggleRow({ title, hint, checked, onChange }) {
    return (
        <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
                <div className="text-xs font-medium text-white/70">{title}</div>
                {hint && <div className="text-[11px] text-white/35 leading-snug">{hint}</div>}
            </div>
            <Toggle checked={checked} onChange={onChange} label={title} />
        </div>
    );
}

function TemplateTile({ active, onClick, bg, label, emoji, dashed }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            className={`relative rounded-xl h-[72px] md:h-20 flex flex-col items-center justify-center gap-0.5 transition-all border-2 ${active
                ? "border-[var(--brand-400,#a78bfa)] ring-2 ring-[rgba(139,92,246,0.25)]"
                : dashed ? "border-dashed border-white/20 hover:border-white/35" : "border-transparent hover:border-white/20"}`}
            style={{ background: bg || "#27272a" }}
        >
            {emoji && <span className="text-lg leading-none">{emoji}</span>}
            <span className="text-[11px] font-bold text-white drop-shadow-md">{label}</span>
        </button>
    );
}

export default function InterstitialModal({ open, redirect, bots = [], onClose, onSaved }) {
    const { showError, showSuccess } = useNotification();
    const slug = redirect?.slug;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [enabled, setEnabled] = useState(false);
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [group, setGroup] = useState("profile");
    const [savedAvatar, setSavedAvatar] = useState(null);
    // Troca de foto só vai pro servidor no "Salvar": { type: 'file'|'bot'|'remove', ... }
    const [pendingAvatar, setPendingAvatar] = useState(null);
    const fileRef = useRef(null);

    const redirectBots = useMemo(
        () => bots.filter((b) => (redirect?.bot_ids || []).includes(b.id)),
        [bots, redirect]
    );
    const photoUrls = useBotPhotos(redirectBots);
    const botsWithPhoto = redirectBots.filter((b) => photoUrls[b.id]);

    useEffect(() => {
        if (!open || !slug) return;
        let mounted = true;
        setLoading(true);
        setPendingAvatar(null);
        authService.redirects
            .get_interstitial(slug)
            .then((res) => {
                if (!mounted || !res.data?.success) return;
                const d = res.data.data;
                setEnabled(!!d.enabled);
                const cfg = normalizeConfig(d.config);
                setConfig(cfg);
                setGroup(templateGroup(cfg.template));
                setSavedAvatar(d.avatar || null);
            })
            .catch(() => showError("Erro ao carregar a página intermediária."))
            .finally(() => mounted && setLoading(false));
        return () => { mounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, slug]);

    // Libera o object URL do preview do arquivo escolhido
    useEffect(() => () => {
        if (pendingAvatar?.type === "file") URL.revokeObjectURL(pendingAvatar.preview);
    }, [pendingAvatar]);

    if (!open) return null;

    const fields = fieldsFor(config.template);
    const has = (f) => fields.includes(f);
    const set = (patch) => setConfig((c) => ({ ...c, ...patch }));

    // Trocar de aba já seleciona o 1º template do grupo (a menos que o atual seja dele)
    function selectGroup(id) {
        setGroup(id);
        if (templateGroup(config.template) !== id) {
            set({ template: (id === "profile" ? PROFILE_TEMPLATES : INTERACTIVE_TEMPLATES)[0].id });
        }
    }
    const groupTemplates = group === "profile" ? PROFILE_TEMPLATES : INTERACTIVE_TEMPLATES;

    const avatarPreview =
        pendingAvatar?.type === "remove" ? null : pendingAvatar?.preview || savedAvatar;

    function onPickFile(e) {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        const err = validateAvatarFile(file);
        if (err) { showError(err); return; }
        setPendingAvatar({ type: "file", file, preview: URL.createObjectURL(file) });
    }

    async function save() {
        setSaving(true);
        try {
            if (pendingAvatar?.type === "file") {
                const r = await authService.redirects.upload_interstitial_avatar(slug, pendingAvatar.file);
                setSavedAvatar(r.data?.data?.avatar || null);
            } else if (pendingAvatar?.type === "bot") {
                const r = await authService.redirects.interstitial_avatar_from_bot(slug, pendingAvatar.botId);
                setSavedAvatar(r.data?.data?.avatar || null);
            } else if (pendingAvatar?.type === "remove") {
                await authService.redirects.delete_interstitial_avatar(slug);
                setSavedAvatar(null);
            }
            setPendingAvatar(null);
            const res = await authService.redirects.update_interstitial(slug, buildConfigPayload(enabled, config));
            if (!res.data?.success) throw new Error(res.data?.message);
            // Mantém o modal aberto para seguir ajustando; só avisa o sucesso.
            showSuccess(enabled ? "Página intermediária salva e ativa." : "Página intermediária salva (desativada).");
            onSaved?.(res.data.data);
        } catch (err) {
            showError(err?.response?.data?.message || err?.message || "Erro ao salvar a página intermediária.");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 z-[99999] flex items-end md:items-center justify-center md:p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={saving ? undefined : onClose} />
            <div
                className="relative w-full max-w-5xl rounded-t-2xl md:rounded-2xl border border-white/[0.09] bg-[#10141a] shadow-[0_24px_64px_rgba(0,0,0,0.55)] text-white flex flex-col"
                style={{ maxHeight: "94dvh", colorScheme: "dark" }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="interstitial-title"
            >
                {/* Header */}
                <div className="flex items-start justify-between gap-3 p-4 md:p-6 pb-4 border-b border-white/[0.06]">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[rgba(139,92,246,0.12)] border border-[rgba(139,92,246,0.18)] flex items-center justify-center text-[#a78bfa]">
                            <Eye size={20} />
                        </div>
                        <div className="min-w-0">
                            <h2 id="interstitial-title" className="text-lg font-semibold">Página intermediária</h2>
                            <p className="text-sm text-white/50 truncate">
                                Exige um toque do visitante antes de abrir o Telegram · <span className="text-white/70">/r/{slug}</span>
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} disabled={saving} className="action-btn flex-shrink-0" aria-label="Fechar">
                        <X size={18} />
                    </button>
                </div>

                <div className="overflow-y-auto overscroll-contain p-4 md:p-6">
                    {loading ? (
                        <Loading text="Carregando..." />
                    ) : (
                        <div className="space-y-5">
                            {/* Ativar: a linha inteira é o switch */}
                            <button
                                type="button"
                                role="switch"
                                aria-checked={enabled}
                                aria-label="Ativar página intermediária"
                                onClick={() => setEnabled((v) => !v)}
                                className={`w-full flex items-center gap-3 p-4 rounded-xl border text-left transition-all ${enabled
                                    ? "bg-[#4c1d95] border-[#6d28d9]"
                                    : "bg-white/[0.02] border-white/[0.06] hover:border-white/[0.1] hover:bg-white/[0.04]"}`}
                            >
                                <span aria-hidden="true" className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${enabled ? "bg-white/10 text-white" : "bg-white/[0.04] text-white/40"}`}>
                                    <Eye size={20} />
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="block text-sm font-semibold text-white">Ativar página intermediária</span>
                                    <span className="block text-xs text-white/50">
                                        {enabled
                                            ? "Ativa: o visitante interage antes de abrir o Telegram."
                                            : "Desativada, usar o redirecionador padrão."}
                                    </span>
                                </span>
                                <ToggleKnob checked={enabled} />
                            </button>

                            <div
                                data-interstitial-body
                                aria-disabled={!enabled}
                                className={`grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6 transition-opacity ${enabled ? "" : "opacity-50 pointer-events-none select-none"}`}
                            >
                                <div className="min-w-0">
                                    <fieldset disabled={!enabled || saving} className="rounded-xl border border-white/[0.06] bg-[#141414] p-4 md:p-5 space-y-5 min-w-0">
                                        {/* Templates: abas Perfil / Interativos */}
                                        <div>
                                            <div role="tablist" aria-label="Tipo de template" className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-white/[0.03] border border-white/[0.06] mb-2">
                                                {GROUPS.map((g) => {
                                                    const active = group === g.id;
                                                    return (
                                                        <button
                                                            key={g.id}
                                                            type="button"
                                                            role="tab"
                                                            aria-selected={active}
                                                            onClick={() => selectGroup(g.id)}
                                                            className={`h-10 md:h-9 rounded-lg text-sm font-semibold transition ${active
                                                                ? "bg-[rgba(139,92,246,0.18)] border border-[rgba(139,92,246,0.4)] text-white"
                                                                : "border border-transparent text-white/55 hover:text-white/80"}`}
                                                        >
                                                            {g.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <p className="text-[11px] text-white/35 mb-3">{GROUPS.find((g) => g.id === group).hint}</p>
                                            <div role="tabpanel" className="grid grid-cols-3 gap-2">
                                                {groupTemplates.map((t) => (
                                                    <TemplateTile
                                                        key={t.id}
                                                        label={t.label}
                                                        emoji={t.emoji}
                                                        bg={t.id === "custom" ? config.custom_color : t.bg}
                                                        dashed={t.id === "custom"}
                                                        active={config.template === t.id}
                                                        onClick={() => set({ template: t.id })}
                                                    />
                                                ))}
                                            </div>
                                        </div>

                                        {has("custom_color") && (
                                            <div>
                                                <label className={labelCls} htmlFor="sx-custom-color">Cor de fundo</label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        id="sx-custom-color"
                                                        type="color"
                                                        value={config.custom_color}
                                                        onChange={(e) => set({ custom_color: e.target.value.toLowerCase() })}
                                                        className="w-11 h-11 md:w-10 md:h-10 rounded-lg bg-transparent border border-white/[0.1] cursor-pointer p-0.5"
                                                    />
                                                    <code className="text-xs text-white/50">{config.custom_color}</code>
                                                </div>
                                            </div>
                                        )}

                                        {has("avatar") && (
                                            <div>
                                                <span className={labelCls}>Foto de perfil</span>
                                                <div className="flex items-center gap-3 flex-wrap">
                                                    <div className="w-16 h-16 rounded-full overflow-hidden bg-white/[0.06] border border-white/[0.1] flex-shrink-0 flex items-center justify-center text-white/30 text-xl font-bold">
                                                        {avatarPreview ? <img src={avatarPreview} alt="" className="w-full h-full object-cover" /> : "?"}
                                                    </div>
                                                    <div className="flex gap-2 flex-wrap">
                                                        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPickFile} />
                                                        <button type="button" onClick={() => fileRef.current?.click()} className="h-9 px-3 inline-flex items-center gap-1.5 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] text-xs font-medium transition">
                                                            <Upload size={13} /> Upload
                                                        </button>
                                                        {avatarPreview && (
                                                            <button type="button" onClick={() => setPendingAvatar({ type: "remove" })} className="h-9 px-3 inline-flex items-center gap-1.5 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-xs font-medium text-[#f87171] transition">
                                                                <Trash2 size={13} /> Remover
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                {botsWithPhoto.length > 0 && (
                                                    <div className="mt-3">
                                                        <div className="text-[11px] text-white/40 mb-1.5">Ou use a foto de um bot deste link:</div>
                                                        <div className="flex gap-2 flex-wrap">
                                                            {botsWithPhoto.map((b) => {
                                                                const active = pendingAvatar?.type === "bot" && pendingAvatar.botId === b.id;
                                                                return (
                                                                    <button
                                                                        key={b.id}
                                                                        type="button"
                                                                        onClick={() => setPendingAvatar({ type: "bot", botId: b.id, preview: photoUrls[b.id] })}
                                                                        className={`inline-flex items-center gap-2 h-9 pl-1 pr-3 rounded-full border text-xs transition max-w-full ${active ? "border-[rgba(139,92,246,0.5)] bg-[rgba(139,92,246,0.15)]" : "border-white/[0.08] bg-white/[0.03] hover:border-white/20"}`}
                                                                    >
                                                                        <img src={photoUrls[b.id]} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                                                                        <span className="truncate">{b.bot_username || b.bot_name}</span>
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                                <p className="text-[11px] text-white/30 mt-1.5">JPG, PNG ou WEBP até 2 MB. A foto fica embutida na página e aparece na hora.</p>
                                            </div>
                                        )}

                                        {has("name") && (
                                            <div>
                                                <label className={labelCls} htmlFor="sx-name">Nome</label>
                                                <input id="sx-name" className={inputCls} placeholder="Ex: Larinne" maxLength={LIMITS.name}
                                                    value={config.name} onChange={(e) => set({ name: e.target.value })} />
                                            </div>
                                        )}

                                        {has("verified") && (
                                            <ToggleRow title="Badge verificado" hint="Mostra selo azul ao lado do nome"
                                                checked={config.verified} onChange={(v) => set({ verified: v })} />
                                        )}

                                        {has("button_text") && (
                                            <div>
                                                <label className={labelCls} htmlFor="sx-btn">Texto do botão</label>
                                                <input id="sx-btn" className={inputCls} placeholder={DEFAULT_CONFIG.button_text} maxLength={LIMITS.button_text}
                                                    value={config.button_text} onChange={(e) => set({ button_text: e.target.value })} />
                                            </div>
                                        )}

                                        {has("show_telegram_icon") && (
                                            <ToggleRow title="Ícone do Telegram no botão" hint="Mostra o avião do Telegram ao lado do texto"
                                                checked={config.show_telegram_icon !== false} onChange={(v) => set({ show_telegram_icon: v })} />
                                        )}

                                        {has("response_time") && (
                                            <div>
                                                <label className={labelCls} htmlFor="sx-rt">Tempo de resposta</label>
                                                <input id="sx-rt" className={inputCls} placeholder="3 minutos" maxLength={LIMITS.response_time}
                                                    value={config.response_time} onChange={(e) => set({ response_time: e.target.value })} />
                                            </div>
                                        )}

                                        {has("countdown_seconds") && (
                                            <div>
                                                <label className={labelCls} htmlFor="sx-cd">Segundos do countdown</label>
                                                <input id="sx-cd" type="number" inputMode="numeric" min={COUNTDOWN_MIN} max={COUNTDOWN_MAX} className={inputCls}
                                                    value={config.countdown_seconds}
                                                    onChange={(e) => set({ countdown_seconds: Math.max(COUNTDOWN_MIN, Math.min(COUNTDOWN_MAX, parseInt(e.target.value, 10) || COUNTDOWN_MIN)) })} />
                                            </div>
                                        )}

                                        {has("auto_redirect") && (
                                            <ToggleRow title="Redirecionar automaticamente"
                                                hint="Não espera o clique: mostra o botão carregando e abre o Telegram sozinho"
                                                checked={config.auto_redirect} onChange={(v) => set({ auto_redirect: v })} />
                                        )}
                                    </fieldset>
                                </div>

                                {/* Preview */}
                                <div className="min-w-0 lg:sticky lg:top-0 lg:self-start">
                                    <span className={labelCls}>Preview</span>
                                    <InterstitialPreview config={config} avatarUrl={avatarPreview} />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Rodapé fixo (mobile friendly) */}
                {!loading && (
                    <div className="flex gap-2 p-4 md:px-6 border-t border-white/[0.06] pb-[max(1rem,env(safe-area-inset-bottom))]">
                        <button type="button" onClick={onClose} disabled={saving} className="btn-secondary flex-1 md:flex-none justify-center">Cancelar</button>
                        <button type="button" onClick={save} disabled={saving} className="btn-primary flex-1 md:flex-none md:ml-auto justify-center inline-flex items-center gap-2">
                            {saving ? <><Loader2 size={15} className="animate-spin" /> Salvando...</> : <><Save size={15} /> Salvar</>}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
